// ============================================================================
//  nav.js — sticky header, active-link highlighting, and the mobile/tablet
//  overlay menu (open/close, scroll lock, focus trap, morphing hamburger).
//    header[data-nav]            the fixed header
//    [data-nav-toggle]           the hamburger button (aria-expanded)
//    [data-nav-menu]             the overlay menu
//    a[data-nav-link][href^="#"] anchor links to sections
// ============================================================================

export function initNav() {
  const header = document.querySelector('[data-nav]');
  if (!header) return;

  // Sticky/condensed state on scroll.
  const onScroll = () => {
    header.classList.toggle('is-scrolled', window.scrollY > 8);
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  // --- Mobile / tablet overlay menu ---------------------------------------
  const toggle = header.querySelector('[data-nav-toggle]');
  const menu = header.querySelector('[data-nav-menu]');

  if (toggle && menu) {
    const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';

    const setOpen = (open) => {
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      menu.classList.toggle('is-open', open);
      // Drives the tablet logo shrink (and any other open-state styling).
      header.classList.toggle('is-menu-open', open);
      // Lock the page behind the overlay.
      document.documentElement.classList.toggle('nav-lock', open);

      if (open) {
        const first = menu.querySelector('a[href], button:not([disabled])');
        if (first) requestAnimationFrame(() => first.focus());
      } else {
        toggle.focus();
      }
    };

    toggle.addEventListener('click', () => setOpen(!isOpen()));

    // Close after choosing a destination (then the page scrolls to it).
    menu.addEventListener('click', (e) => {
      if (e.target.closest('a')) setOpen(false);
    });

    // Esc closes.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen()) setOpen(false);
    });

    // Trap Tab within the header (toggle + overlay) while open.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !isOpen()) return;
      const items = [...header.querySelectorAll('a[href], button:not([disabled])')];
      if (items.length < 2) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Reset if the viewport grows to the desktop layout while open.
    const desktop = window.matchMedia('(min-width: 1024px)');
    desktop.addEventListener('change', () => {
      if (desktop.matches && isOpen()) setOpen(false);
    });
  }

  // --- Active-link highlighting (ScrollSpy) --------------------------------
  const links = [...header.querySelectorAll('a[data-nav-link][href^="#"]')];
  const sections = links
    .map((l) => document.querySelector(l.getAttribute('href')))
    .filter(Boolean);

  if (sections.length) {
    const spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = `#${entry.target.id}`;
          links.forEach((l) =>
            l.classList.toggle('is-active', l.getAttribute('href') === id)
          );
        });
      },
      { rootMargin: '-45% 0px -45% 0px' }
    );
    sections.forEach((s) => spy.observe(s));
  }
}
